import React, { useContext } from 'react';

import { Grid, Skeleton, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from '../../../AdminAuthContainer';
import { ClassSessionContext } from '../../../ClassSessionContext';
import { fromRedToGreen } from '../../../utils/colors';

interface Bin {
    name: string;
    count: number;
    color: string;
}

const HistogramExample = ({ title, data, width, height }) => (
    <div
        style={{
            textAlign: 'center',
            width
        }}
    >
        <h2>{title}</h2>
        <BarChart
            width={width}
            height={height}
            data={data}
            margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
            }}
        >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="count">
                {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
            </Bar>
        </BarChart>
    </div>
);

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

const TimeRowPaper = styled(Paper)`
    margin-top: 12px;
`;

const GridTimeItem = styled(Grid)`
    text-align: center;
    display: flex;
    flex-direction: column;
    padding: 12px;
    align-items: center;
`;

const RowContainer = styled(Grid)`
    padding: 12px;
`;

const TimeRowTitleText = styled(Typography)`
    display: inline;
    font-family: Lato;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: 32px;
    letter-spacing: 0.15000000596046448px;
    text-align: center;
    color: #3a3c41;
    margin-left: 8px;
`;

const TimeRowTitle = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const PaperCircleText = styled(Typography)`
    font-family: Lato;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;
    letter-spacing: 0px;
    text-align: center;
    color: #ffffff;
`;

const PaperCircle = styled(({ circleColor, ...otherProps }) => <Paper {...otherProps} />)`
    margin-top: 7px;
    border-radius: 50%;
    width: 100px;
    height: 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${(props) => props.circleColor};
`;

const renderTimeValueInCircle = (timeString: string) => {
    if (timeString.includes(', ')) {
        const times = timeString.split(', ');
        return (
            <>
                <div>{times[0]},</div>
                <div>{times[1]}</div>
            </>
        );
    }
    return timeString;
};

const TimeRowItem = ({ titleText, value, circleColor, id }) => (
    <GridTimeItem item xs={6} sm={6} md={4} id={id}>
        <TimeRowTitle>
            <TimeRowTitleText variant="h2">{titleText}</TimeRowTitleText>
        </TimeRowTitle>
        <PaperCircle elevation={4} circleColor={circleColor}>
            <PaperCircleText data-testid="value">{renderTimeValueInCircle(value)}</PaperCircleText>
        </PaperCircle>
    </GridTimeItem>
);

export const ClassSessionDetailInsights = () => {
    const { t } = useTranslation('portal');
    const { classSessionData } = useContext(ClassSessionContext);
    const { authMode } = useContainer(AdminAuthContainer);
    console.log('ClassSessionDetailInsights classSessionData', classSessionData, 'authMode', authMode);

    if (classSessionData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    const data: Bin[] = [
        { name: 'F', count: 0, color: fromRedToGreen(0 / 4) },
        { name: 'D', count: 0, color: fromRedToGreen(1 / 4) },
        { name: 'C', count: 0, color: fromRedToGreen(2 / 4) },
        { name: 'B', count: 0, color: fromRedToGreen(3 / 4) },
        { name: 'A', count: 0, color: fromRedToGreen(4 / 4) }
    ];
    classSessionData.scores.forEach((score) => {
        if (score.totalScorePercentage === 0) {
            return;
        }
        data.forEach((bin) => {
            if (score.engagementGrade === bin.name) {
                bin.count += 1;
            }
        });
    });
    return (
        <>
            {classSessionData.averageAggregates?.student && (
                <>
                    <HistogramExample
                        width={window.innerWidth - 325}
                        height={300}
                        title={t('student-engagement-scores')}
                        data={data}
                    ></HistogramExample>
                    <TimeRowPaper>
                        <RowContainer container justifyContent="center">
                            <TimeRowItem
                                id="student-attendance-time"
                                titleText={t('average-student-attendance-time')}
                                value={`${Math.round(
                                    classSessionData.averageAggregates?.student['Attendance Time'] || 0
                                )} min`}
                                circleColor={fromRedToGreen(0 / 5)}
                            />
                            <TimeRowItem
                                id="student-focused-time"
                                titleText={t('average-student-focused-time')}
                                value={`${Math.round(
                                    classSessionData.averageAggregates?.student['Focused Time'] || 0
                                )} min`}
                                circleColor={fromRedToGreen(1 / 5)}
                            />
                            <TimeRowItem
                                id="student-talk-time"
                                titleText={t('average-student-talk-time')}
                                value={`${Math.round(
                                    classSessionData.averageAggregates?.student['Talking Time'] || 0
                                )} min`}
                                circleColor={fromRedToGreen(2 / 5)}
                            />
                        </RowContainer>
                        <RowContainer container justifyContent="center">
                            <TimeRowItem
                                id="student-stars-count"
                                titleText={t('average-student-stars-count')}
                                value={`${Math.round(classSessionData.averageAggregates?.student['Stars Count'] || 0)}`}
                                circleColor={fromRedToGreen(3 / 5)}
                            />
                            <TimeRowItem
                                id="student-raised-hand-count"
                                titleText={t('average-student-raised-hand-count')}
                                value={`${Math.round(
                                    classSessionData.averageAggregates?.student['Raised Hand Count'] || 0
                                )}`}
                                circleColor={fromRedToGreen(4 / 5)}
                            />
                            <TimeRowItem
                                id="student-feedback-count"
                                titleText={t('average-student-feedback-count')}
                                value={`${
                                    Math.round(classSessionData.averageAggregates?.student['Feedback Count'] || 0) +
                                    Math.round(classSessionData.averageAggregates?.student['Reaction Count'] || 0)
                                }`}
                                circleColor={fromRedToGreen(5 / 5)}
                            />
                        </RowContainer>
                    </TimeRowPaper>
                </>
            )}
        </>
    );
};
